import { type EventChainDoneMessage, WS_MESSAGE_TYPE } from 'cpool-constants';

import { wsClient, type WsIncomingMessage, type WsMessageHandler } from './wsClient';

/**
 * Event-chain feature built on the shared {@link wsClient}. After a command returns its `id` (the
 * correlation id), callers subscribe to that id and are notified once the backend reports the whole
 * event chain finished — replacing position-polling with a push.
 *
 * `eventChainWsHandler` is registered on the shared client for `WS_MESSAGE_TYPE.EVENT_CHAIN`; the
 * `eventChainSocket` API below is what mutations use. The backend answers late subscribers from a
 * short-lived marker, so subscribing right after the command response is safe.
 */
type Listener = (message: EventChainDoneMessage) => void;

const listeners = new Map<string, Set<Listener>>();

/** correlation ids we still want the server to notify us about (re-sent on every reconnect) */
const pending = new Set<string>();

const sendSubscribe = (correlationId: string) => wsClient.send({ correlationId, type: WS_MESSAGE_TYPE.EVENT_CHAIN });

const removeListener = (correlationId: string, listener: Listener) => {
    const set = listeners.get(correlationId);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) {
        listeners.delete(correlationId);
        pending.delete(correlationId);
    }
};

/** Subscribe to a correlation chain; returns an unsubscribe function. */
const subscribe = (correlationId: string, listener: Listener): (() => void) => {
    pending.add(correlationId);
    let set = listeners.get(correlationId);
    if (!set) {
        set = new Set();
        listeners.set(correlationId, set);
    }
    set.add(listener);
    // ensure the shared socket is up and the server knows we're waiting
    wsClient.open();
    sendSubscribe(correlationId);
    return () => removeListener(correlationId, listener);
};

/**
 * Resolve when the chain finishes, or with `null` after `timeoutMs` (the fallback that lets callers
 * proceed if the socket is down or the notification never arrives).
 */
const waitForCompletion = (
    correlationId: string,
    { timeoutMs }: { timeoutMs: number },
): Promise<EventChainDoneMessage | null> =>
    new Promise((resolve) => {
        let settled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let unsubscribe: (() => void) | undefined;
        const finish = (message: EventChainDoneMessage | null) => {
            if (settled) return;
            settled = true;
            if (timer) clearTimeout(timer);
            unsubscribe?.();
            resolve(message);
        };
        unsubscribe = subscribe(correlationId, finish);
        timer = setTimeout(() => finish(null), timeoutMs);
    });

/** Handler registered on the shared {@link wsClient} for `WS_MESSAGE_TYPE.EVENT_CHAIN` messages. */
export const eventChainWsHandler: WsMessageHandler = {
    onMessage: (message: WsIncomingMessage) => {
        const { correlationId } = message;
        if (typeof correlationId !== 'string') return;

        const set = listeners.get(correlationId);
        if (!set) return;

        // one-shot: deliver, then forget so a reconnect does not re-subscribe to a finished chain
        pending.delete(correlationId);
        listeners.delete(correlationId);
        set.forEach((listener) => listener(message as EventChainDoneMessage));
    },
    // re-establish still-pending subscriptions after every (re)connect
    onOpen: () => pending.forEach((correlationId) => sendSubscribe(correlationId)),
};

export const eventChainSocket = { subscribe, waitForCompletion };
